import { SubCategory } from "@shared/types";
import { gql, useQuery } from "@shared/utils";
import {
  Box,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import React, { FunctionComponent, useMemo, useState } from "react";

import { useGqlField } from "../../../../hooks";
import VariablePopup from "../DynamicVariables/VariablePopup";

import { DVInlineProps } from "./types";
import { VariableButton } from "./VariableButton";

const EDIT_SUB_CATEGORY_ID = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, subcategoryId: $value }) {
      _id
      subcategoryId
    }
  }
`;

const EDIT_OPTION_ID = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, optionId: $value }) {
      _id
      optionId
    }
  }
`;

const EDIT_CATEGORY_ID = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, categoryId: $value }) {
      _id
      categoryId
    }
  }
`;
const EDIT_OUTPUT = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, output: $value }) {
      _id
      output
    }
  }
`;
const CATEGORYS = gql`
  query Categorys($productId: String!) {
    categorys(product: $productId) {
      _id
      name
    }
  }
`;
const CATEGORY = gql`
  query Category($id: ID!) {
    category(_id: $id) {
      _id
      name
      SubCategorys {
        _id
        name
        Options {
          _id
          name
        }
      }
    }
  }
`;

const outputs: Array<{ label: string; value: string }> = [
  {
    value: "VALUE",
    label: "Value",
  },
];

export const DVInlineStaticSubCategory: FunctionComponent<DVInlineProps> = ({
  dynamicVariable,
  productId: productIdTrue,
  parentProductId,
  onVariableDelete,
  removeDynamicVariable,
  activeDynamicVariableId,
  setActiveDynamicVariableId,
  onChange,
  variables,
}) => {
  const [open, setOpen] = useState(false);
  const fromParent = useMemo(() => {
    return /^PARENT/.test(dynamicVariable.variableType);
  }, [dynamicVariable.variableType]);

  const productId = useMemo(() => {
    return fromParent ? parentProductId : productIdTrue;
  }, [fromParent, parentProductId, productIdTrue]);

  const { data } = useQuery<{
    category?: {
      SubCategorys: Array<{ _id: string; name: string }>;
      _id: string;
      name: string;
    };
  }>(CATEGORY, { variables: { id: dynamicVariable.categoryId } });

  const subcategory = (data?.category?.SubCategorys || []).find(
    (s) => s._id === dynamicVariable.subcategoryId
  );

  const [categoryId, setCategoryId] = useGqlField(
    dynamicVariable.categoryId,
    dynamicVariable._id,
    EDIT_CATEGORY_ID
  );
  const [subcategoryId, setSubCategoryId] = useGqlField(
    dynamicVariable.subcategoryId,
    dynamicVariable._id,
    EDIT_SUB_CATEGORY_ID
  );
  const [optionId, setOptionId] = useGqlField(
    dynamicVariable.optionId,
    dynamicVariable._id,
    EDIT_OPTION_ID
  );
  const [output, setOutput] = useGqlField(
    dynamicVariable.output,
    dynamicVariable._id,
    EDIT_OUTPUT
  );
  const { data: categoryData } = useQuery<{
    categorys?: Array<{ _id: string; name: string }>;
  }>(CATEGORYS, { variables: { productId }, fetchPolicy: 'cache-and-network' });

  const categorys: Array<{ _id: string; name: string }> = [
    ...(categoryData?.categorys || []),
  ].sort((a, z) => (a.name > z.name ? 1 : -1));
  const { data: subData } = useQuery<{
    category?: {
      SubCategorys: SubCategory[];
      _id: string;
      name: string;
    };
  }>(CATEGORY, { variables: { id: dynamicVariable.categoryId } });
  const subcategories = [...(subData?.category?.SubCategorys || [])].sort(
    (a, z) => (a.name > z.name ? 1 : -1)
  );
  const onModalClose = async () => {
    if (categoryId === "" || subcategoryId === "" || optionId === "") {
      variables &&
        onChange?.(variables.filter((v) => v !== dynamicVariable._id));
      await removeDynamicVariable?.({
        variables: {
          input: {
            id: dynamicVariable._id,
          },
        },
      });
      onVariableDelete?.(dynamicVariable._id);
    }
  };

  return (
    <VariablePopup
      actions={[]}
      title="Edit Static Specification"
      dynamicVariable={dynamicVariable}
      activeDynamicVariableId={activeDynamicVariableId}
      onCancel={() => {
        setOpen(false);
        onModalClose();
      }}
      onSave={() => {
        setOpen(false);
      }}
      onClose={() => {
        setOpen(false);
      }}
      setActiveDynamicVariableId={setActiveDynamicVariableId}
      forceOpen={open}
    >
      <VariableButton
        typeLabel={fromParent ? "Parent Spec" : "Specification"}
        variableType="STATIC_SUBCATEGORY"
        onClick={() => {
          setOpen(true);
        }}
        line1={data?.category?.name}
        line2={subcategory?.name}
        output={dynamicVariable?.output}
        fromParent={fromParent}
      />
      <Box>
        {categoryData?.categorys && (
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel>Component</InputLabel>
            <Select
              label="Component"
              onChange={(e, _v) => {
                setCategoryId(e.target.value);
              }}
              value={categoryId}
            >
              {categorys.map((t: { _id: string; name: string }) => (
                <MenuItem value={t._id} key={t._id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {subData?.category?.SubCategorys && (
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel>Specification</InputLabel>
            <Select
              label="Specification"
              onChange={(e, _v) => {
                setSubCategoryId(e.target.value);
              }}
              value={subcategoryId}
            >
              {subcategories.map((o: { _id: string; name: string }) => {
                return (
                  <MenuItem value={o._id} key={o._id}>
                    {o.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {subcategoryId && (
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel>Options</InputLabel>
            <Select
              label="Options"
              onChange={(e) => {
                setOptionId(e.target.value);
              }}
              value={optionId}
            >
              {subcategories
                .filter((sc) => sc._id === subcategoryId)[0]
                ?.Options?.map((o: { _id: string; name: string }) => {
                  return (
                    <MenuItem value={o._id} key={o._id}>
                      {o.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel>Output</InputLabel>
          <Select
            label="Output"
            onChange={(e, _v) => {
              setOutput(e.target.value);
            }}
            value={output}
          >
            {outputs.map((o: { label: string; value: string }) => {
              return (
                <MenuItem value={o.value} key={o.value}>
                  {o.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Alert severity="info" sx={{ margin: 2 }}>
          Static Specification, will always output the selected value of the
          specification, no matter what the configuration has, this is useful if
          you are trying to see if an option equals an output ( and not useful
          for Exists or Not Exists)
        </Alert>
      </Box>
    </VariablePopup>
  );
};
